<template>
  <div class="tiptap-editor">
    <EditorContent 
      :editor="editor" 
      :class="editable ? 'w-full flex-1 bg-pitch-black min-h-44 text-lg text-total-white py-2 pt-4 px-3 focus:outline-none resize-none' : 'w-full text-base leading-6 text-start'"
    />
  </div>
</template>

<script setup>
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { Plugin } from 'prosemirror-state'
import { watch, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  content: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Start your message here'
  },
  editable: {
    type: Boolean,
    default: true
  },
  onImageUpload: {
    type: Function,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'input'])

// Custom extension for handling image paste and drop (only for editable mode)
const ImageUpload = Image.extend({
  addProseMirrorPlugins() {
    if (!props.editable || !props.onImageUpload) {
      return []
    }
    
    return [
      new Plugin({
        props: {
          handleDOMEvents: {
            paste: (view, event) => {
              const items = Array.from(event.clipboardData?.items || [])
              const imageItems = items.filter(item => item.type.startsWith('image/'))
              
              if (imageItems.length > 0) {
                event.preventDefault()
                
                imageItems.forEach(async (item) => {
                  const file = item.getAsFile()
                  if (file) {
                    try {
                      const imageUrl = await props.onImageUpload(file)
                      if (imageUrl) {
                        const { schema } = view.state
                        const node = schema.nodes.image.create({ src: imageUrl })
                        const transaction = view.state.tr.replaceSelectionWith(node)
                        view.dispatch(transaction)
                      }
                    } catch (error) {
                      console.error('Error uploading image:', error)
                    }
                  }
                })
                return true
              }
              return false
            },
            
            drop: (view, event) => {
              const files = Array.from(event.dataTransfer?.files || [])
              const imageFiles = files.filter(file => file.type.startsWith('image/'))
              
              if (imageFiles.length > 0) {
                event.preventDefault()
                
                const coordinates = view.posAtCoords({ 
                  left: event.clientX, 
                  top: event.clientY 
                })
                
                if (coordinates) {
                  imageFiles.forEach(async (file) => {
                    try {
                      const imageUrl = await props.onImageUpload(file)
                      if (imageUrl) {
                        const { schema } = view.state
                        const node = schema.nodes.image.create({ src: imageUrl })
                        const transaction = view.state.tr.insert(coordinates.pos, node)
                        view.dispatch(transaction)
                      }
                    } catch (error) {
                      console.error('Error uploading image:', error)
                    }
                  })
                }
                return true
              }
              return false
            }
          }
        }
      })
    ]
  }
})

const editor = useEditor({
  content: props.editable ? props.modelValue : props.content,
  editable: props.editable,
  extensions: [
    StarterKit,
    ImageUpload.configure({
      inline: true,
      allowBase64: true,
      HTMLAttributes: {
        class: 'inline-image max-w-full h-auto rounded my-2',
      },
    }),
    ...(props.editable ? [
      Placeholder.configure({
        placeholder: props.placeholder,
        emptyEditorClass: 'is-editor-empty',
      })
    ] : []),
  ],
  editorProps: {
    attributes: {
      class: 'tiptap-content',
    },
  },
  onUpdate: ({ editor }) => {
    if (props.editable) {
      const html = editor.getHTML()
      emit('update:modelValue', html)
      emit('input')
    }
  },
})

// Watch for external changes
watch(() => props.editable ? props.modelValue : props.content, (newValue) => {
  if (editor.value && editor.value.getHTML() !== newValue) {
    editor.value.commands.setContent(newValue, false)
  }
})

onBeforeUnmount(() => {
  if (editor.value) {
    editor.value.destroy()
  }
})

// Expose editor instance for parent component
defineExpose({
  editor,
  focus: () => editor.value?.commands.focus(),
  blur: () => editor.value?.commands.blur(),
  getHTML: () => editor.value?.getHTML(),
  getText: () => editor.value?.getText(),
  setContent: (content) => editor.value?.commands.setContent(content)
})
</script>

<style scoped>
.tiptap-editor {
  @apply w-full flex-1 flex flex-col;
}

/* Dark theme styles for Tiptap */
:deep(.ProseMirror) {
  outline: none;
  border: none;
  text-align: left;
}

/* Composing mode (editable) */
.tiptap-editor:has([contenteditable="true"]) :deep(.ProseMirror) {
  @apply bg-pitch-black text-total-white text-lg leading-6;
  padding: 0.5rem 0.75rem;
  padding-top: 1rem;
  min-height: 11rem; /* min-h-44 equivalent */
  flex: 1;
  text-align: left;
}

/* Viewing mode (non-editable) */
.tiptap-editor:has([contenteditable="false"]) :deep(.ProseMirror) {
  @apply text-total-white text-base leading-6;
  padding: 0;
  text-align: left;
}

:deep(.ProseMirror p.is-editor-empty:first-child::before) {
  @apply text-total-white opacity-50;
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

:deep(.ProseMirror img) {
  @apply max-w-full h-auto rounded my-2;
  display: inline-block;
}

:deep(.ProseMirror p) {
  margin: 0;
  line-height: 1.5;
  margin-bottom: 0.5rem;
  text-align: left;
}

:deep(.ProseMirror br) {
  display: block;
  content: "";
  margin-top: 0.5em;
}

/* Ensure proper text color inheritance and left alignment */
:deep(.ProseMirror *) {
  color: inherit;
  text-align: left;
}

/* Handle placeholder styling */
:deep(.is-editor-empty::before) {
  color: rgba(255, 255, 255, 0.5);
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
}

/* Focus styles */
:deep(.ProseMirror:focus) {
  outline: none;
}

/* Handle lists and other formatting for viewing mode */
:deep(.ProseMirror ul),
:deep(.ProseMirror ol) {
  padding-left: 1.5rem;
  margin-bottom: 0.5rem;
}

:deep(.ProseMirror blockquote) {
  border-left: 3px solid #ccc;
  margin: 1rem 0;
  padding-left: 1rem;
}

:deep(.ProseMirror h1),
:deep(.ProseMirror h2),
:deep(.ProseMirror h3),
:deep(.ProseMirror h4),
:deep(.ProseMirror h5),
:deep(.ProseMirror h6) {
  font-weight: bold;
  margin-bottom: 0.5rem;
}

/* Enhanced inline image styling for both modes */
:deep(.ProseMirror img),
:deep(.inline-image) {
  @apply max-w-full h-auto rounded my-2;
  display: inline-block;
  vertical-align: top;
}

/* Ensure images in content are properly displayed */
:deep(.ProseMirror img[src]) {
  max-width: 100%;
  height: auto;
  border-radius: 0.375rem;
  margin: 0.5rem 0;
  display: block;
}
</style> 